/**
 * PortCallDistributionChart - Bar chart showing distribution
 *
 * For port mode: Shows vessels by country
 * For vessel mode: Shows most visited ports
 */
import { useMemo } from 'react';
import { Card, CardHeader, CardTitle } from '@ais-ui/ui-core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { DistributionItem, AnalyticsMode } from './types';
import { useLocalization } from '@ais-ui/locale-management';

interface PortCallDistributionChartProps {
  distribution: DistributionItem[];
  mode: AnalyticsMode;
  maxItems?: number;
  height?: number;
}

const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
];

export const PortCallDistributionChart = ({
  distribution,
  mode,
  maxItems = 10,
  height,
}: PortCallDistributionChartProps) => {
  const { messages } = useLocalization();
  const title = mode === 'port' ? 'Vessels by Country' : 'Most Visited Ports';

  const entityName = mode === 'port' ? 'vessel' : 'port';
  const noDataMessage =
    messages.emptyStateNoData?.replace('{entity}', entityName) ??
    `No ${entityName} data available`;

  const chartData = useMemo(() => {
    return distribution.slice(0, maxItems).map((item, index) => ({
      ...item,
      // Ensure label is never empty - use 'Unknown' as fallback
      label: item.label || 'Unknown',
      fill: COLORS[index % COLORS.length],
    }));
  }, [distribution, maxItems]);

  const othersCount = useMemo(() => {
    if (distribution.length <= maxItems) return 0;
    return distribution
      .slice(maxItems)
      .reduce((sum, item) => sum + item.count, 0);
  }, [distribution, maxItems]);

  if (distribution.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <div className="px-6 pb-4 h-[200px] flex items-center justify-center text-muted-foreground">
          {noDataMessage}
        </div>
      </Card>
    );
  }

  // Calculate dynamic height based on number of items (min 35px per bar)
  // Use provided height prop if available, otherwise calculate dynamically
  const calculatedHeight = Math.max(200, chartData.length * 35);
  const chartHeight = height ?? calculatedHeight;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <div className="px-6 pb-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 80, bottom: 10 }}
          >
            <XAxis type="number" allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="label"
              width={75}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number) => [value, 'Calls']}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {othersCount > 0 && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            +{distribution.length - maxItems} more ({othersCount} calls)
          </p>
        )}
      </div>
    </Card>
  );
};
